import { createSignal, createUniqueId, For, type JSX, onCleanup, onMount, Show } from "solid-js";
import { useScene } from "~/hooks/useScene";

// About how long one tab's scene takes to play out before the tour moves on.
const SCENE_MS = 6500;

/**
 * Tabs over a set of scenes. On first view the tour plays each tab's scene once
 * in turn and then rests on the first; picking a tab stops the tour and replays
 * that scene from the start. The tabs are real controls; each scene stays one
 * picture to assistive tech.
 */
export const SceneTabs = <T,>(props: {
  items: T[];
  label: string;
  tab: (item: T) => JSX.Element;
  children: (item: T) => JSX.Element;
  /** Told of every switch, the tour's included, so the page around the scenes can follow along. */
  onSelect?: (item: T) => void;
}) => {
  const tour = useScene();
  const id = createUniqueId();
  const [selected, setSelected] = createSignal(0);
  // Bumped on every switch so the keyed scene below remounts and its CSS animations restart.
  const [run, setRun] = createSignal(0);
  const tabs: HTMLButtonElement[] = [];
  let touring = true;

  const show = (index: number) => {
    setSelected(index);
    setRun(run() + 1);
    props.onSelect?.(props.items[index]);
  };

  const pick = (index: number) => {
    touring = false;
    show(index);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    const last = props.items.length - 1;
    const targets: Partial<Record<string, number>> = {
      ArrowRight: selected() === last ? 0 : selected() + 1,
      ArrowLeft: selected() === 0 ? last : selected() - 1,
      Home: 0,
      End: last,
    };
    const next = targets[e.key];
    if (next === undefined) return;
    e.preventDefault();
    pick(next);
    tabs[next]?.focus();
  };

  onMount(() => {
    if (!tour.armed()) return;
    let elapsed = 0;
    const timer = window.setInterval(() => {
      if (!touring) return window.clearInterval(timer);
      if (!tour.inView()) return;
      elapsed += 250;
      if (elapsed < SCENE_MS) return;
      elapsed = 0;
      const next = selected() + 1;
      if (next >= props.items.length) touring = false;
      show(next % props.items.length);
    }, 250);
    onCleanup(() => window.clearInterval(timer));
  });

  return (
    <div ref={tour.ref} class="flex flex-col gap-3">
      <div
        role="tablist"
        aria-label={props.label}
        onKeyDown={onKeyDown}
        class="flex items-center gap-1 self-start rounded-xl border border-primary-border bg-tile p-1 shadow-sm"
      >
        <For each={props.items}>
          {(item, i) => (
            <button
              ref={(el) => {
                tabs[i()] = el;
              }}
              type="button"
              role="tab"
              id={`${id}-tab-${i()}`}
              aria-selected={selected() === i()}
              aria-controls={`${id}-panel`}
              tabIndex={selected() === i() ? 0 : -1}
              onClick={() => pick(i())}
              class="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
              classList={{
                "bg-panel text-primary-text shadow-sm": selected() === i(),
                "text-secondary-text hover:text-primary-text": selected() !== i(),
              }}
            >
              {props.tab(item)}
            </button>
          )}
        </For>
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-tab-${selected()}`}>
        <Show when={`${selected()}:${run()}`} keyed>
          {props.children(props.items[selected()])}
        </Show>
      </div>
    </div>
  );
};
